import { Button, Col, Row, Table, Pagination as Paging } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/layout/PageHeader';
import { createDataset, deleteDataset, renameDataset } from '../../../lib/api/dataset-api';
import { userIsAdmin } from '../../../lib/util';
import { Dataset } from '../../../types/dataset';
import { useAuthenticationContext } from '../../authentication/AuthenticationContext';
import { CreateNewDataset } from './CreateNewDataset';
import { GetProjectOverviewTableColumns } from './ProjectOverviewTable';
import { useGetProjectAndDatasets } from '../../../lib/hooks';
import { ErrorText } from '../../../components/text/ErrorText';
import { RenameDataset } from './RenameDataset';
import { Pagination } from '../../../types';

const defaultPaging = {
  pageSize: 10,
  page: 1,
};

const ProjectOverview = () => {
  let history = useHistory();
  const location = useLocation();

  const { currentUser } = useAuthenticationContext();

  const { projectId } = useParams() as { projectId: string };
  const [creatorVisible, setCreatorVisible] = useState<boolean>(false);
  const [renameVisible, setRenameVisible] = useState<boolean>(false);
  const [datasetForRename, setDatasetForRename] = useState<Dataset | null>(null);
  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);

  const { project, datasets, totalCount, loading, refetch, error } = useGetProjectAndDatasets(
    projectId,
    paginationObject
  );

  const onCreateDataset = async (name: string) => {
    await createDataset(name, projectId);
    refetch();
  };

  const onClickUrl = useCallback(
    (record: Dataset) => {
      return `/projects/${projectId}/datasets/${record.id}/overview`;
    },
    [projectId]
  );

  const onDelete = async (e: Event, dataset: Dataset) => {
    e.preventDefault();
    e.stopPropagation();
    await deleteDataset(dataset.id);
    refetch();
  };

  const onShowRenamePopup = async (e: Event, dataset: Dataset) => {
    setRenameVisible(true);
    setDatasetForRename(dataset);
  };

  const onRenameDataset = async (name: string) => {
    if (datasetForRename) {
      await renameDataset(datasetForRename.id, name);
    }
    refetch();
  };

  useEffect(() => {
    setPaginationObject(defaultPaging);
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      history.replace({
        pathname: location.pathname,
        search: 'page=' + paginationObject.page + '&size=' + paginationObject.pageSize,
      });
    }
  }, [projectId, history, location.pathname, paginationObject, refetch]);

  return (
    <>
      <PageHeader
        container="Datasets"
        header={loading ? 'Loading...' : project?.name || 'No project found'}
        description={project ? 'These are the datasets that are part of project ' + project.name : ''}
      />
      <Row className="my-3">
        <Col span={12}></Col>
        <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setCreatorVisible(true)}
            disabled={!userIsAdmin(currentUser)}
            type="primary"
            size={'large'}>
            Create new dataset
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {error ? (
            <ErrorText>Something went wrong. We could not find the documents you were looking for.</ErrorText>
          ) : (
            <>
              <Table
                className="ant-table-toolbox"
                columns={GetProjectOverviewTableColumns(onClickUrl, onDelete, onShowRenamePopup)}
                rowKey={record => record.id}
                dataSource={datasets}
                loading={loading}
                pagination={false}
              />
              {!loading && (
                <div className="flex justify-end my-4">
                  <Paging
                    showSizeChanger={false}
                    defaultCurrent={1}
                    current={paginationObject.page}
                    pageSize={defaultPaging.pageSize}
                    total={totalCount}
                    onChange={page => setPaginationObject({ ...defaultPaging, page: page })}
                  />
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
      <CreateNewDataset
        title="Create New Dataset"
        visible={creatorVisible}
        onClose={() => setCreatorVisible(false)}
        onSave={(name: string) => {
          onCreateDataset(name);
          setCreatorVisible(false);
        }}
      />
      <RenameDataset
        dataset={datasetForRename!}
        title="Rename Dataset"
        visible={renameVisible}
        onClose={() => setRenameVisible(false)}
        onSave={(name: string) => {
          onRenameDataset(name);
          setRenameVisible(false);
        }}
      />
    </>
  );
};

export default ProjectOverview;
